import clsx from 'clsx';
import Divider from 'components/Divider';
import { Modal } from 'components/Modal/Modal';
import Tag from 'components/Tag/Tag';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { observer } from 'mobx-react-lite';
import { Checkmark, Close } from 'react-ionicons';
import { useCartContext } from '../CartProvider';
import { useState } from 'react';
import Loader from 'components/Loader/Loader';

interface ServiceOptimizationModalProps {
  isOpen: boolean;
  close: () => void;
  onCancel?: () => void;
  onConfirm?: () => void;
}

const features = [
  'Monthly price',
  'All access types',
  'Single edgeboot port',
  'Multiple edgeboot ports',
  'Ping monitoring',
  'Advanced network telemetry',
  'API Access',
];

const ServiceOptimizationModal = observer(
  ({ isOpen, close }: ServiceOptimizationModalProps) => {
    const { keepMnsOptimization, revertMnsOptimization } = useCartContext();
    const [loadingAction, setLoadingAction] = useState<
      'keep' | 'revert' | null
    >(null);

    const handleRevert = async () => {
      try {
        setLoadingAction('revert');
        await revertMnsOptimization();
        close();
      } finally {
        setLoadingAction(null);
      }
    };

    const handleKeep = async () => {
      try {
        setLoadingAction('keep');
        await keepMnsOptimization();
        close();
      } finally {
        setLoadingAction(null);
      }
    };

    return (
      <Modal
        isVisible={isOpen}
        close={close}
        className="vertical-scrollbar max-h-[90vh] max-w-[628px] overflow-y-auto"
      >
        <div className="flex flex-col gap-2 rounded-t-lg bg-background-base-surface-3 px-8 py-6">
          <h1 className="text-[28px] leading-9 text-content-base-default">
            We optimized your service selection{' '}
          </h1>
          <p className="font-semibold leading-[22px] text-content-base-subdued">
            Network monitoring add-on services
          </p>
        </div>
        <div className="flex flex-col gap-12 p-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <p className="text-base font-semibold leading-6 text-content-base-default">
                We&apos;ve updated your network monitoring setup by replacing
                the two MNS Essentials add-ons with a single MNS Enhanced
                service.
              </p>
              <p className="text-base font-semibold leading-6 text-content-base-default">
                Granite recommends this solution when two circuits are ordered
                at the same location — offering dual WAN monitoring and a lower
                total monthly price.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex h-full items-end gap-3 self-stretch rounded border border-stroke-base-subdued p-6">
                {/* feature list */}
                <div className="flex flex-1 flex-col items-start gap-4">
                  <h1 className="text-base font-bold text-content-base-default">
                    Features
                  </h1>
                  <div className="flex flex-col items-start gap-4">
                    {features.map((feature, i) => (
                      <p key={i} className="text-sm text-content-base-default">
                        {feature}
                      </p>
                    ))}
                  </div>
                </div>
                <Divider
                  layout="vertical"
                  className="!h-[280px] self-stretch"
                />
                {/* essentials */}
                <div className="flex w-[124px] flex-col items-center gap-4">
                  <Tag
                    content={{
                      type: 'label',
                      label: `Essentials`,
                    }}
                    status="custom"
                    className={clsx('!bg-[#8989FF] !text-[#191925]')}
                  />
                  <div className="flex flex-col items-center gap-4">
                    <p className="font-bold text-content-base-default">
                      $39.98*
                    </p>
                    <StatusIconsRow
                      icons={[
                        'close',
                        'check',
                        'close',
                        'check',
                        'close',
                        'close',
                      ]}
                    />
                  </div>
                </div>
                <Divider
                  layout="vertical"
                  className="!h-[280px] self-stretch"
                />

                {/* enhanced */}
                <div className="flex w-[124px] flex-col items-center gap-4">
                  <Tag
                    content={{
                      type: 'label',
                      label: `Enhanced`,
                    }}
                    status="custom"
                    className={clsx('!bg-[#A7FFB2] !text-[#191925]')}
                  />
                  <div className="flex flex-col items-center gap-4">
                    <p className="font-bold text-content-base-default">
                      $34.99
                    </p>
                    <StatusIconsRow icons={Array(6).fill('check')} />
                  </div>
                </div>
              </div>
              <p className="text-xs text-content-base-subdued">
                *Monthly price reflects two MNS Essentials packages ($19.99
                each)
              </p>
            </div>
          </div>
          <div className="flex items-start gap-5 self-stretch">
            <GraniteButton
              variant="secondary"
              size="large"
              onClick={handleRevert}
              disabled={loadingAction !== null}
              className={clsx(loadingAction === 'revert' && 'loading-button')}
            >
              Revert changes
              {loadingAction === 'revert' && (
                <Loader animationClassname="!w-3 !h-3" />
              )}
            </GraniteButton>

            <GraniteButton
              variant="primary"
              size="large"
              onClick={handleKeep}
              disabled={loadingAction !== null}
              className={clsx(loadingAction === 'keep' && 'loading-button')}
            >
              Keep changes
              {loadingAction === 'keep' && (
                <Loader animationClassname="!w-3 !h-3" />
              )}
            </GraniteButton>
          </div>
        </div>
      </Modal>
    );
  },
);

export default ServiceOptimizationModal;

const StatusIconsRow = ({ icons }: { icons: ('check' | 'close')[] }) => (
  <>
    {icons.map((type, i) =>
      type === 'check' ? (
        <Checkmark key={i} width="20px" height="20px" color="#F8FAFC" />
      ) : (
        <Close key={i} width="20px" height="20px" color="#FF5F82" />
      ),
    )}
  </>
);
